import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { isAdminUser, UserRole } from '../sp-types/userType';

interface AdminGuardProps {
    children: ReactNode;
    allowedRoles?: UserRole[];
    fallbackPath?: string;
}

/**
 * AdminGuard - Protects routes that require admin access
 * Use inside AuthGuard (assumes user is already authenticated)
 * 
 * Usage:
 *   <AdminGuard>...</AdminGuard>                    // Requires admin or superadmin
 *   <AdminGuard allowedRoles={['superadmin']}>     // Requires superadmin only
 */
const AdminGuard: React.FC<AdminGuardProps> = ({
    children,
    allowedRoles,
    fallbackPath = '/dashboard'
}) => {
    const { data: user, isLoading } = useUser();

    // Show loading spinner while loading or if user not yet available
    if (isLoading || !user) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    // Check access
    let hasAccess = false;

    if (allowedRoles && allowedRoles.length > 0) {
        // Check specific roles
        hasAccess = user.role ? allowedRoles.includes(user.role) : false;
    } else {
        // Default: check if admin (supports legacy isAdmin field too)
        hasAccess = isAdminUser(user);
    }

    if (!hasAccess) {
        console.warn('🚫 [ADMIN GUARD] Access denied for user:', user.email, 'role:', user.role);
        return <Navigate to={fallbackPath} replace />;
    }

    return <>{children}</>;
};

export default AdminGuard;
